import { ChevronDown } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useT } from '../../i18n/t'

const aboutChildren = [
  { labelKey: 'nav.aboutOverview', href: '/o-projekcie' },
  { labelKey: 'nav.aboutNews', href: '/o-projekcie/aktualnosci' },
  { labelKey: 'nav.aboutRanking', href: '/o-projekcie/ranking' },
] as const

function isAboutActive(pathname: string): boolean {
  return pathname === '/o-projekcie' || pathname.startsWith('/o-projekcie/')
}

type NavAboutMenuProps = {
  variant: 'desktop' | 'mobile'
  onNavigate?: () => void
}

export function NavAboutMenu({ variant, onNavigate }: NavAboutMenuProps) {
  const { t } = useT()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const active = isAboutActive(pathname)

  useEffect(() => {
    if (variant !== 'desktop' || !open) return
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, variant])

  useEffect(() => {
    queueMicrotask(() => setOpen(false))
  }, [pathname])

  if (variant === 'mobile') {
    return (
      <div className="border-b border-ink/10">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-4 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
          aria-expanded={open}
          aria-controls={menuId}
        >
          <span className={['display text-[28px] leading-none', active ? 'text-ink' : 'text-ink/60'].join(' ')}>
            {t('nav.about')}
          </span>
          <ChevronDown
            className={['h-5 w-5 shrink-0 text-ink/45 transition', open ? 'rotate-180' : ''].join(' ')}
            aria-hidden
          />
        </button>
        <div
          id={menuId}
          className={['grid transition-[grid-template-rows] duration-200', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'].join(
            ' ',
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-1 pb-4 pl-2">
              {aboutChildren.map(({ labelKey, href }) => (
                <NavLink
                  key={href}
                  to={href}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    [
                      'rounded-lg px-3 py-2.5 text-[15px] font-semibold transition',
                      isActive ? 'bg-ink/8 text-ink' : 'text-ink/55 hover:bg-ink/5 hover:text-ink',
                    ].join(' ')
                  }
                >
                  {t(labelKey)}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={menuId}
        className={[
          'inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 lg:px-3',
          active ? 'bg-ink/8 text-ink' : 'text-ink/70 hover:bg-ink/5 hover:text-ink',
        ].join(' ')}
      >
        {t('nav.about')}
        <ChevronDown className={['h-4 w-4 opacity-60 transition', open ? 'rotate-180' : ''].join(' ')} aria-hidden />
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 top-[calc(100%+6px)] z-[60] min-w-[200px] overflow-hidden pds-surface bg-paper py-1 shadow-[0_18px_50px_rgba(35,35,35,0.12)]"
        >
          {aboutChildren.map(({ labelKey, href }) => (
            <Link
              key={href}
              to={href}
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onNavigate?.()
              }}
              className={[
                'block px-4 py-2.5 text-sm font-semibold transition hover:bg-ink/5',
                pathname === href ? 'text-ink' : 'text-ink/70',
              ].join(' ')}
            >
              {t(labelKey)}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}
